<template>
  <div>
    <div v-if="globalLoading" class="spinner-overlay">
      <v-progress-circular
        indeterminate
        color="primary"
        size="50"
        width="4"
      />
    </div>

  <v-alert v-if="isApprovalLocked" type="warning" dense class="mb-2">
    This affiliate is in {{ item.data.status }} status. Only users with Review Approval Users permission can update it.
  </v-alert>

  <v-tabs
      color="primary"
      v-model="currentTab"
      @change="changeTab"
      next-icon="mdi-arrow-right-bold-box-outline"
      prev-icon="mdi-arrow-left-bold-box-outline"
      show-arrows
  >
    <v-tab key="general">General</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="info">Info</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="address" @change="getAddress">Address</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="contacts">Contacts</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="campaigns">Campaigns</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="accounting">Accounting</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="referral" @change="getReferralSettings">Referral</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="history" @change="getHistory">History</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="risk" @change="getRiskCases">Risk</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="note" @change="getNotes">{{noteTabName}}</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="pings" @change="getPings">Pings</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="defaultPrograms" @change="getProgramsData">Default Programs</v-tab>
    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="affiliateVerticals" @change="getAffiliateVerticalsData">Verticals</v-tab>
    <v-tab
      v-if="user[0].can('see_all_affiliates_pii')"
      key="topCountries"
      @change="getTopCountries"
    >
      Top Countries
    </v-tab>

    <v-tab v-if="user[0].can('see_all_affiliates_pii')" key="affiliateCap"   @change="getAffiliateCap">Caps</v-tab>
    <v-tab-item :key="`affiliate-${item.data.id}-general`">
      <v-progress-linear v-show="preloaderChart" indeterminate />
      <v-container fluid>
        <v-row class="py-4">
          <v-col cols="12" sm="6">
            <v-row align="center">
              <v-col cols="12" sm="8">
                <v-text-field label="ID" hide-details readonly disabled :value="item.data.id"></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field label="Name" hide-details v-model="name"></v-text-field>
              </v-col>
                <v-col cols="12" sm="8">
                    <v-text-field label="Email" hide-details v-model="email"></v-text-field>
                </v-col>
              <v-col cols="12" sm="8">
                <v-select
                  hide-details
                  label="Affiliate Type"
                  :items="affiliateTypes"
                  v-model="affiliateType"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <template v-if="platform === 1">
                  <v-select
                    hide-details
                    label="Traffic Type"
                    v-model="trafficType"
                    :items="trafficTypes"
                  />
                </template>

                <template v-else>
                  <v-autocomplete
                    v-model="selectedTrafficTypes"
                    :items="trafficTypes"
                    label="Traffic Type"
                    multiple
                    chips
                    small-chips
                    :menu-props="{ maxHeight: '400' }"
                    class="custom_autocomplete"

                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        small
                        label
                        close
                        @click:close="removeTrafficType(index)"
                      >
                        {{ item }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </template>

              </v-col>
              <template v-if="platform === 2">
                <v-col cols="12" sm="8">
                  <v-text-field label="Company Name" hide-details v-model="companyName"></v-text-field>
                </v-col>
              </template>

              <template v-if="platform === 2">
                <v-col cols="12" sm="8">
                  <v-select
                    hide-details
                    label="Source"
                    :items="['Forum', 'Affiliate Manager', 'Conference', 'Social Media', 'Tech Partner', 'Other']"
                    v-model="source"
                  />
                </v-col>
              </template>

              <v-col cols="12" sm="8">
                <div v-for="(url, index) in postbackUrls" :key="index">
                  <div style="width: 90%; display: inline-block">
                    <v-text-field
                            :label="'Postback ' + (index + 1)"
                            hide-details
                            v-model="postbackUrls[index]"
                    />
                  </div>
                  <div style="width: 10%; display: inline-block; text-align: right">
                    <v-btn icon @click="removePostbackUrl(index)" style="margin-top: 10px">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
                <v-btn @click="addPostbackUrl">Add Postback</v-btn>
              </v-col>
              <v-col cols="12" sm="8">
                <v-select hide-details label="Trends" v-model="chartOptions.chartPeriod" :items="[{value: 7, text: 'Last 7 Days'}]" />
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field label="Tier / Highest Tier" hide-details disabled readonly :value="combinedTier"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6">
            <v-row>
              <v-col cols="12" sm="8">
                <v-select
                  hide-details
                  label="AM"
                  :items="managers"
                  v-model="managerId"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <v-select
                    hide-details
                    label="Status"
                    :items="statuses"
                    v-model="status"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <v-select
                  v-model="currency"
                  :items=currencies
                  item-value="id"
                  item-text="name"
                  label="Currency"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="8">
                <v-select
                  hide-details
                  label="Billing Cycle"
                  :items="[{value:'monthly', text:'monthly'}, {value:'weekly', text:'weekly'}, {value:'semimonthly', text:'semimonthly'}]"
                  v-model="billingCycle"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <v-select
                        hide-details
                        label="Payment Term"
                        :items="paymentTerms"
                        v-model="paymentTermId"
                />
              </v-col>
                <v-col cols="12" sm="8">
                    <v-text-field
                        v-model="this.item.data.paymentType"
                        label="Payment Type"
                        readonly
                        disabled
                    ></v-text-field>
                </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                    :disabled="!this.user[0].can('aff_min_amount')"
                    label="Min. Amount Override "
                    :value="this.item.data.minAmountOverride"
                    v-model="minAmountOverride"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8" class="pb-0 pt-5">
                <div class="d-flex justify-space-between align-center">
                  <span class="inactive_offer_text">Block Payments</span>
                  <v-switch
                      v-model="paymentsBlocked"
                      :disabled="!user[0].can('block_payments')"
                      class="ma-0"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-btn
          v-if="!checkInputsNotChanged"
          fixed
          top
          right
          style="top: 50px"
          color="primary"
          :loading="updating"
          :disabled="updating || isApprovalLocked"
          class="upd-btn ma-0"
          @click="onUpdate"
        >
          Update
        </v-btn>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-skeleton-loader :loading="chartLoading" class="mx-auto mt-3" width="100%" height="250px" type="image">
              <apexchart type="line" height="250px" :options="chartOptions" :series="series"></apexchart>
            </v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-info`">
      <v-progress-linear v-show="preloaderInfo" indeterminate />
      <v-container fluid>
        <v-simple-table class="card-simple-table">
          <tbody>
            <tr>
              <td>First Name:</td>
              <td><strong>{{affiliateInfo.firstName}}</strong></td>
            </tr>
            <tr>
              <td>Last Name:</td>
              <td><strong>{{affiliateInfo.lastName}}</strong></td>
            </tr>
            <tr>
              <td>Promoted Verticals:</td>
              <td>
                <strong v-if="affiliateInfo.verticals">{{affiliateInfo.verticals.join(', ')}}</strong>
              </td>
            </tr>
            <tr>
              <td>Promoted Traffic:</td>
              <td><strong v-if="affiliateInfo.trafficTypes">{{affiliateInfo.trafficTypes.join(', ')}}</strong></td>
            </tr>
            <tr>
              <td>Expected Monthly Revenue:</td>
              <td><strong>{{affiliateInfo.expectedMonthlyRevenue}}</strong></td>
            </tr>
            <tr>
              <td>Top Countries:</td>
              <td><strong>{{affiliateInfo.topCountries}}</strong></td>
            </tr>
            <tr>
              <td>Websites:</td>
              <td><strong v-if="affiliateInfo.websites">{{affiliateInfo.websites.join(', ')}}</strong></td>
            </tr>
            <tr>
              <td>Other networks:</td>
              <td><strong v-if="affiliateInfo.otherNetworks">{{affiliateInfo.otherNetworks.join(', ')}}</strong></td>
            </tr>
            <tr>
              <td>Top offers, brands:</td>
              <td><strong>{{affiliateInfo.topPromotedBrands}}</strong></td>
            </tr>
            <tr>
              <td>Learn Source:</td>
              <td><strong>{{affiliateInfo.learnSource}}</strong></td>
            </tr>
            <tr>
              <td>Payment Method:</td>
              <td><strong>{{affiliateInfo.paymentMethod}}</strong></td>
            </tr>
            <template v-if="affiliateInfo.paymentDetails">
              <tr v-for="(paymetItem, index) in affiliateInfo.paymentDetails"
                  :key="`affiliate-${item.data.id}-info-pd-${index}`">
                <td style="text-transform: capitalize">{{paymetItem.field.replaceAll('_', ' ')}}:</td>
                <td><strong>{{paymetItem.value}}</strong></td>
              </tr>
            </template>
            <tr>
              <td>IM Type:</td>
              <td><strong>{{affiliateInfo.imType}}</strong></td>
            </tr>
            <tr>
              <td>IM Username:</td>
              <td><strong>{{affiliateInfo.imUsername}}</strong></td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-address`">
      <v-progress-linear v-show="preloaderAddress" indeterminate />
      <v-container fluid>
        <v-simple-table class="card-simple-table">
          <tbody>
            <tr>
              <td>Street Address:</td>
              <td><strong>{{affiliateInfo.streetAddress || 'N/A'}}</strong></td>
            </tr>
            <tr>
              <td>Street Address 2:</td>
              <td><strong>{{affiliateInfo.streetAddress2 || 'N/A'}}</strong></td>
            </tr>
            <tr>
              <td>City:</td>
              <td><strong>{{affiliateInfo.city || 'N/A'}}</strong></td>
            </tr>
            <tr>
              <td>State/Province:</td>
              <td><strong>{{affiliateInfo.state || 'N/A'}}</strong></td>
            </tr>
            <tr>
              <td>Postal Code:</td>
              <td><strong>{{affiliateInfo.postalCode || 'N/A'}}</strong></td>
            </tr>
            <tr>
              <td>Country:</td>
              <td><strong>{{affiliateInfo.country || 'N/A'}}</strong></td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-contacts`">
      <v-progress-linear v-show="preloaderInfo" indeterminate />
      <v-container fluid>
        <template v-if="this.affiliateContacts">
          <v-row>
            <v-col cols="12">
              <strong>Contacts:</strong>
            </v-col>

            <v-col cols="12">
              <v-btn
                  v-if="isAffiliateContactsChanged()"
                  fixed
                  top
                  right
                  style="top: 50px"
                  color="primary"
                  class="upd-btn ma-0"
                  @click="onUpdateAffiliateContacts"
              >
                Update
              </v-btn>


            </v-col>
          </v-row>
          <template>
            <div>
              <v-row v-for="(contact, index) in affiliateContacts" :key="`affiliate-${item.data.id}-contact-${index}`" align="center">
                <v-col cols="6">
                  <v-text-field v-model="affiliateContacts[index].email" label="Email"></v-text-field>
                </v-col>
                <v-col cols="1">
                  <v-radio-group v-model="affiliateContacts[index].isPrimary" @change="setPrimaryContact(index)" row>
                    <v-radio :label="contact.isPrimary ? 'Primary' : ''" :value="true"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="1" class="d-flex align-end">
                  <v-btn color="primary" icon @click="resetPassword(affiliateContacts[index].email)">
                    <v-icon>mdi-lock-reset</v-icon>
                  </v-btn>
                  <v-btn color="primary" icon @click="confirmDeleteContact(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-dialog v-model="dialog" max-width="500px">
                <v-card>
                  <v-card-title class="headline">Confirm Deletion</v-card-title>
                  <v-card-text>Are you sure you want to delete this contact?</v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="green darken-1" text @click="dialog = false">Cancel</v-btn>
                    <v-btn color="green darken-1" text @click="deleteContact(selectedContactIndex)">Confirm</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
          </template>

          <v-row>
            <v-col cols="12">
              <v-btn color="primary" class="white--text"  @click="addContact">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
              <br>
              <span v-if="validation.length > 0" class="red--text">{{ validation.join(', ') }}</span>
            </v-col>

          </v-row>
        </template>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-offers`">
      <v-progress-linear v-show="preloaderAffiliate"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
        <v-skeleton-loader :loading="affiliateLoading" class="mb-2 mx-auto" max-width="100%" max-height="320px"
                           type="table-thead, table-tbody">
          <v-data-table
              :headers="cardAffiliate.headers"
              :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
              :items="cardAffiliate.items"
              :footer-props="{itemsPerPageOptions: cardAffiliate.pageOptions}"
              :page="1"
              :items-per-page="10"
              loading-text="Loading... Please wait"
              fixed-header
              :class="`elevation-0 mb-0 co-data-table items-size-45`"
              item-class="td_title"
              color="#7F98A5"
          >
            <template v-for="item of cardAffiliate.headers" :slot="`header.${[item.value]}`" slot-scope="{header}">
              <span class="header_title">{{header.text}}</span>
            </template>
            <template v-for="header of cardAffiliate.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
              <template v-if="header.value === 'currencyId'">
                <v-tooltip top open-delay="400">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{item.currencySymbol}}</span>
                  </template>
                  <span>{{item.currencyName}}</span>
                </v-tooltip>
              </template>
              <template v-else>{{item[header.value]}}</template>
            </template>
<!--              body-->
            <template v-for="header of cardAffiliate.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
              <template v-if="header.value === 'currency'">
                <v-tooltip top open-delay="400">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{item.currency}}</span>
                  </template>
                  <span>{{item.currencyName}}</span>
                </v-tooltip>
              </template>
              <template v-if="header.value === 'name'">
                <span class="campaign-item" v-if="user[0].can('manage_campaigns')" @click="openCampaignCard(item)">{{ item.name }}</span>
              </template>
              <template v-else>{{item[header.value]}}</template>
            </template>
          </v-data-table>
        </v-skeleton-loader>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-accounting`">
      <v-progress-linear v-show="preloaderAccounting"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
        <v-skeleton-loader v-if="accountingLoading" class="mb-2 mx-auto"
                           max-width="100%" max-height="320px"
                           type="table-heading@4"
        >
          <v-data-table
              :headers="cardAffiliateAccounting.headers"
              :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
              :items="cardAffiliateAccounting.items"
              :footer-props="{itemsPerPageOptions: cardAffiliateAccounting.pageOptions}"
              :page="1"
              :items-per-page="25"
              loading-text="Loading... Please wait"
              fixed-header
              :class="`elevation-0 mb-0 co-data-table items-size-45`"
              item-class="td_title"
              color="#7F98A5"
          >
            <template v-for="item of cardAffiliateAccounting.headers" :slot="`header.${[item.value]}`" slot-scope="{header}">
              <span class="header_title">{{header.text}}</span>
            </template>
            <template v-for="header of cardAffiliateAccounting.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
              <template v-if="header.value === 'start_period' || header.value === 'end_period'">
                {{ getPretyDate(item[header.value]) }}
              </template>
              <template v-else-if="header.value === 'amount'">
                {{item.symbol}}{{ (item.items_amount - item.carry_over_amount + (item.carry_over_modifications || 0)).toFixed(2) }}
              </template>
              <template v-else-if="header.value === 'referral_commission'">
                {{item.symbol}}{{ (item.referral_commission).toFixed(2)  }}
              </template>
              <template v-else-if="header.value === 'modifications'">
                {{item.symbol}}{{ (Number(item.modifications || 0) - (item.carry_over_modifications || 0)).toFixed(2) }}
              </template>
              <template v-else-if="header.value === 'carry_over_amount'">
                {{item.symbol}}{{ (item.carry_over_amount + item.carry_over_referral_commission).toFixed(2) }}
              </template>
              <template v-else-if="header.value === 'total_revenue'">
                {{item.symbol}}{{ item.total_revenue.toFixed(2)  }}
              </template>
              <template v-else-if="header.value === 'partial_payments'">
                {{ item.symbol }}{{ item.status === 'closed' ? item.total_revenue.toFixed(2) : Number(item.partial_payments).toFixed(2) }}
              </template>
              <template v-else>
                {{ item[header.value] }}
              </template>
            </template>
          </v-data-table>

        </v-skeleton-loader>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-referral`">
      <v-progress-linear
          v-show="preloaderReferral"
          indeterminate
      />
      <v-row class="pa-4">
        <v-col cols="6" class="pb-0">
          <h5>Family</h5>
          <v-row>
            <v-col cols="12">
              <p class="mb-0 subtitle">Parent</p>
              <div
                v-if="referralTab.family.parent"
              >
                <v-icon color="primary" @click="showCard(referralTab.family.parent)">
                  mdi-file-account-outline
                </v-icon>
                <span>
                  {{ referralTab.family.parent }}
                </span>
              </div>
              <div v-else>
                No parent account yet
              </div>
            </v-col>
            <v-col cols="12">
              <p class="mb-0 subtitle">Children</p>
              <div
                v-for="child in referralTab.family.children"
              >
                <v-icon color="primary" @click="showCard(child)">
                  mdi-file-account-outline
                </v-icon>
                <span>
                  {{ child }}
                </span>
              </div>
              <div v-if="referralTab.family.children.length === 0">
                No children accounts yet
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="6" class="pb-0">
          <h5>Settings</h5>
          <v-checkbox
            label="Enable referral commission"
            color="primary"
            v-model="referralTab.settings.isCommissionEnabled"
            :disabled="!user[0].can('enable_referral_commission')"
          />
          <v-checkbox
            label="Earnings optional"
            color="primary"
            v-model="referralTab.settings.isOptionalEarningsEnabled"
            :disabled="!referralTab.settings.isCommissionEnabled || !user[0].can('referral_optional_sales')"
            class="mt-0"
          />
          <v-subheader class="pl-2">
            Commission: {{ referralTab.settings.commission }}%
          </v-subheader>
          <v-slider
            v-model="referralTab.settings.commission"
            :min="sliderSettings.min"
            :max="sliderSettings.max"
            :step="sliderSettings.step"
            :disabled="!referralTab.settings.isCommissionEnabled || !user[0].can('set_referral_percent')"
            color="primary"
          />
        </v-col>
        <v-col v-if="referralTab.isSettingsChanged" cols="12" class="py-0">
          <v-btn
              color="primary"
              :disabled="isApprovalLocked"
              @click="updateReferralSettings"
          >
            Update
          </v-btn>
        </v-col>
      </v-row>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-history`">
      <v-progress-linear v-show="preloaderHistory"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
          <v-data-table
            :headers="historyTab.headers"
            :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
            :items="historyTab.items"
            :footer-props="{itemsPerPageOptions: historyTab.pageOptions}"
            :page="1"
            :items-per-page="25"
            loading-text="Loading... Please wait"
            fixed-header
            :class="`elevation-0 mb-0 co-data-table items-size-45`"
            item-class="td_title"
            color="#7F98A5"
          >
            <template v-slot:item.dateAdded="{ item }" >
              {{ (new Date(item.dateAdded * 1000)).toLocaleDateString("en-US") }}
            </template>
            <template v-slot:item.logs="{ item }" >
              <HistoryLog :logsString='item.logs' ></HistoryLog>
            </template>
          </v-data-table>

      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-risk`">
      <v-progress-linear v-show="preloaderRisk" indeterminate />
      <v-container fluid class="pa-0">
        <div class="d-flex justify-space-between px-3 align-content-center">
          <h2 class="mb-0">Risk</h2>
          <CaseModal :affiliate="item.data" :userName="user[0].name" :campaigns="cardAffiliate.items"/>
        </div>
        <v-data-table
          :headers="riskTab.headers"
          :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
          :items="riskTab.items"
          :footer-props="{itemsPerPageOptions: riskTab.pageOptions}"
          :page="1"
          :items-per-page="25"
          loading-text="Loading... Please wait"
          fixed-header
          :class="`elevation-0 mb-0 co-data-table items-size-45`"
          item-class="td_title"
          color="secondary"
        >
        </v-data-table>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-note`">
      <v-progress-linear v-show="preloaderNotes"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
        <div class="d-flex justify-space-between px-3 align-content-center">
          <h2 class="mb-0">Notes</h2>
          <NoteModal :affiliate="item.data" :user="user[0]" v-on:createNote="createNote"/>
        </div>
        <v-data-table
                :headers="noteTab.headers"
                :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
                :items="noteTab.items"
                :footer-props="{itemsPerPageOptions: riskTab.pageOptions}"
                :page="1"
                :items-per-page="25"
                loading-text="Loading... Please wait"
                fixed-header
                :class="`elevation-0 mb-0 co-data-table items-size-45`"
                item-class="td_title"
                color="secondary"
        >
          <template  v-slot:item.dateAdded="{ item }">
            {{item.dateAdded}}
            <div>
              <NoteEditModal :note="item" :user="user[0]" v-on:createNote="createNote"/>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <button
                          v-bind="attrs"
                          v-on="on"
                          @click="manageNote(item, 'delete')"
                          :disabled="user[0].email !== item.email"
                          class="td_title btn btn-link"
                  >
                    <i class="far fa-trash"></i>
                  </button>
                </template>
                <span>Delete Note</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-pings`">
      <v-progress-linear v-show="preloaderPings"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
        <div class="d-flex justify-space-between px-3 align-content-center">
        </div>
        <v-data-table
                :headers="pingsTab.headers"
                :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
                :items="pingsTab.items"
                :footer-props="{itemsPerPageOptions: riskTab.pageOptions}"
                :page="1"
                :items-per-page="25"
                loading-text="Loading... Please wait"
                fixed-header
                :class="`elevation-0 mb-0 co-data-table items-size-45`"
                item-class="td_title"
                color="secondary"
        >
          <template v-for="header of pingsTab.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
            <template v-if="header.value === 'url'">
              <short-text :text="item[header.value]" noCopy></short-text>
            </template>
            <template v-if="header.value === 'lastAttempt' || header.value === 'dateAdded'">
              {{item[header.value].replace('+00:00', 'UTC')}}
            </template>
            <template v-else>
              {{item[header.value]}}
            </template>
          </template>
        </v-data-table>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-defaultPrograms`">
      <div>
        <v-progress-linear v-show="preloaderPrograms"
                           indeterminate
        ></v-progress-linear>
        <v-container>
          <v-row style="width: 100%; margin-left: 5px; border-bottom: 2px solid gray">
            <v-col cols="5" class="pt-2 font-weight-black" style="color: #7F98A5; min-width: 220px; text-align: center">Vertical</v-col>
            <v-col cols="5" class="pt-2 font-weight-black" style="color: #7F98A5; min-width: 220px; text-align: center" >Default Program</v-col>
            <v-col cols="2" class="pt-2 pl-10 font-weight-black" style="color: #7F98A5; text-align: center">Action</v-col>
          </v-row>
          <v-row style="margin-top: 20px">
            <v-row  v-for="(pair, index) in selectPairs" :key="index" style="width: 100%">
              <v-col cols="5">
                <div class="condition__controls">
                  <select
                    class="condition__dimension-name condition__matches custom-select ml-2"
                    style="width: 100% !important;"
                    v-model="pair.vertical"
                    :disabled = "!user[0].can('manage_default_programs')"
                  >
                    <option :value="null">-- Select vertical --</option>
                    <option v-for="vertical in verticals" :value="vertical.id">{{ vertical.name }}</option>
                  </select>
                </div>
              </v-col>
              <v-col cols="5">
                <div class="condition__controls">
                  <select
                    class="condition__dimension-name condition__matches custom-select ml-2"
                    style="width: 100% !important;"
                    v-model="pair.program"
                    :disabled = "!user[0].can('manage_default_programs')"
                  >
                    <option :value="null">-- Select program --</option>
                    <option v-for="program in programs" :value="program.id">{{ program.name }}</option>
                  </select>
                </div>
              </v-col>
              <v-col cols="2" style="text-align: center" v-if="user[0].can('manage_default_programs')">
                <div class="condition__controls">
                  <span class="trash action" @click="deleteSelectPair(index)">
                  <i class="fas fa-trash" aria-hidden="true"></i>
                </span>
                </div>
              </v-col>
            </v-row>
          </v-row>
          <div style="margin-top: 30px">
            <button class="btn-add" @click="addSelectPair" v-if="user[0].can('manage_default_programs')">
              + Add Default
            </button>
          </div>
          <v-btn color="primary" class="save-btn" @click="saveDefaultPrograms" v-if="user[0].can('manage_default_programs')" :disabled="isApprovalLocked">
            save
          </v-btn>
          <div v-if="showDefaultProgramError" style="color: red">Please, check uniqueness </div>
        </v-container>
      </div>
    </v-tab-item>
    <v-tab-item :key="`affiliate-verticals-${item.data.id}`">
      <div  style="margin-top: 30px; height: 600px;">
        <v-container>
          <v-progress-linear v-show="preloaderAffVerticals" indeterminate></v-progress-linear>

          <v-autocomplete
              v-model="selectedVerticals"
              :items="filteredVerticals"
              item-text="name"
              multiple
              chips
              small-chips
              :menu-props="{ maxHeight: '400' }"
              class="custom_autocomplete"
              @change="updateSelectedVerticals"
              :search-input.sync="searchTerm"
              @keyup.enter="addNewVertical"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                  small
                  label
                  close
                  @click:close="removeVertical(index)"
              >
                {{ item.name }}
              </v-chip>
            </template>
            <template v-slot:no-data>
              <span>Press enter to create new item</span>
            </template>
          </v-autocomplete>

          <v-btn @click="updateAction" color="primary" class="my-3" :disabled="isUpdateDisabled">Update</v-btn>

          <div v-if="error" class="error-message">{{ error }}</div>
        </v-container>
      </div>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-topCountries`">
      <v-row class="mb-4">
        <v-col cols="12">
          <h4>Current Top Countries</h4>
          <div v-if="combinedCountries.length">
            <v-chip
              v-for="(country, index) in combinedCountries"
              :key="index"
              class="ma-1"
              small
              label
            >
              {{ country.name }}
            </v-chip>
          </div>
          <div v-else class="text--secondary">
            No top countries set.
          </div>
        </v-col>

      </v-row>

      <div style="margin-top: 30px; height: 600px;">
        <v-container>
          <v-progress-linear v-show="preloaderTopCountries" indeterminate></v-progress-linear>

          <v-autocomplete
            v-model="selectedCountries"
            :items="filteredCountriesList"
            item-text="code"
            multiple
            chips
            small-chips
            :menu-props="{ maxHeight: '400' }"
            class="custom_autocomplete"
            :search-input.sync="searchCountryTerm"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                small
                label
                close
                @click:close="removeCountry(index)"
              >
                {{ item.name }}
              </v-chip>
            </template>
          </v-autocomplete>

          <v-btn @click="updateCountriesAction" color="primary" class="my-3" :disabled="isCountriesUpdateDisabled">
            Update
          </v-btn>

          <div v-if="countryError" class="error-message">{{ countryError }}</div>
        </v-container>
      </div>
    </v-tab-item>


    <v-tab-item :key="`affiliate-${item.data.id}-caps`">
      <v-container fluid>
        <v-row align="center">
          <v-col cols="12" sm="12" class="py-0">
            <v-checkbox
                v-model="caps.isCapEnabled"
                label="Caps enabled"
                color="orange darken-3"
                hide-details
            ></v-checkbox>
            <div v-if="caps.isCapEnabled" class="">
              <v-container>
                <v-row align="center" justify-md="start">
                  <v-checkbox v-model="caps.useDates" hide-details label="Use dates"></v-checkbox>
                </v-row>
                <v-row align="center" justify-md="start" v-if="caps.useDates">
                  <v-col md="6">
                    <v-menu
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                            label="Start date"
                            readonly
                            :value="caps.startDate"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          locale="en-in"
                          no-title
                          @input="updStart"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col md="6">
                    <v-menu
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                            label="End date"
                            readonly
                            :value="caps.endDate"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          locale="en-in"
                          no-title
                          @input="updEnd"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row align="center" justify="start">
                  <v-col md="12"><h6>Sales</h6></v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Day" v-model="caps.salesDay"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Week" v-model="caps.salesWeek"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Month" v-model="caps.salesMonth"></v-text-field>
                  </v-col>
                </v-row>
                <v-row align="center" justify="start">
                  <v-col md="12">
                      <custom-select
                          :options="getExitTrafficOffers"
                          label="nameId"
                          @search="exitTrafficOffersUpd"
                          v-model="caps.salesRedirectOfferId"
                          :filterable="false"
                          :clearable="false"
                          class="custom_select"
                      />
                  </v-col>
                </v-row>

                <v-divider class="divider"></v-divider>

                <v-row align="center" justify="start">
                  <v-col md="12"><h6>Clicks</h6></v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Day" v-model="caps.clicksDay"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Week" v-model="caps.clicksWeek"></v-text-field>
                  </v-col>
                  <v-col md="4">
                    <v-text-field type="number" label="Amount Month" v-model="caps.clicksMonth"></v-text-field>
                  </v-col>
                </v-row>
                <v-row align="center" justify="start">
                  <v-col md="12">
                    <custom-select
                      :options="getExitTrafficOffers"
                      label="nameId"
                      @search="exitTrafficOffersUpd"
                      v-model="caps.clicksRedirectOfferId"
                      :filterable="false"
                      :clearable="false"
                      class="custom_select"
                      append-to-body
                    />
                  </v-col>
                </v-row>
                <v-divider class="divider"></v-divider>
              </v-container>
            </div>
            <div class="">
              <v-btn
                  fixed
                  top
                  right
                  style="top: 50px"
                  color="primary"
                  class="upd-btn ma-0"
                  :disabled="isApprovalLocked"
                  @click="updateCaps"
              >
                Update
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-tab-item>
  </v-tabs>
  </div>
</template>

<script>
import campaignsApi from "@/api/campaigns";
import paymentsApi from "@/api/payments";
import programsApi from "@/api/programs";
import verticalsApi from "@/api/verticals";
import affiliatesApi from "@/api/affiliates";
import {DateTime} from "luxon";
import reports from "@/api/reports";
import store from "@/store";
import {mapGetters, mapMutations, mapState} from "vuex";
import {createPeriod, getCookie} from "@/helpers";
import affiliates from "@/api/affiliates";
import CaseModal from './CaseModal';
import NoteModal from './NoteModal';
import NoteEditModal from './NoteEditModal';
import { generateLinkWithParams } from "@/helpers/linkGenerator";
import HistoryLog from "@/components/common/HistoryLog";
import {debounce} from "debounce";
import {getCurrencyList} from "@/helpers";

export default {
  props: {
    item: {
      type: Object
    }
  },
  components: {
    CaseModal,
    NoteModal,
    NoteEditModal,
    HistoryLog
  },
  async mounted() {
    this.globalLoading = true;
    this.managers = this.affiliateManagers.map( man =>({
      value: man.id, text: man.name
    }));

    if (!this.paymentTerms.length) {
      await this.getPaymentTerms();
    }

    const postbacks = await affiliates.getAffiliatePostbacks(this.item.data.id);
    this.postbackUrls = postbacks.map(p => p.postback);
    this.initialPostbacks = [...this.postbackUrls];

    let filter = !!this.item.data.affiliate_id ? this.item.data.affiliate_id : this.item.data.id;
    const tab = this.item.data.tab && this.item.data.tab === 'notes' && this.user[0].can('see_all_affiliates_pii') ? 7 : undefined;
    this.preloaderChart = true
    try {
      this.currencies = getCurrencyList();
      const items = await affiliates.getAffiliatesProfileCO({
        payload: {
          limit: 1,
          page: 1,
          orderBy: '',
          orderDirectionAsc: false,
          filters: {'affNameId': `${filter}`}
        }
      });
      this.item.data = items.data[0];
      this.status = this.item.data.status;
      this.name = this.item.data.name;
      this.billingCycle = this.item.data.billingCycle;
      this.minAmountOverride = this.item.data.minAmountOverride;
      this.managerId = this.item.data.affiliateManagerId;
      this.postbackUrl = this.item.data.postbackUrl;
      this.currency = this.item.data.currencyId;
      this.platform = await affiliates.getPlatform()
      this.affiliateContacts = await affiliates.affiliateContacts(filter)
      this.affiliateContactsOrigin = [...this.affiliateContacts]
    } catch (error) {
      console.error(error);
      store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
    } finally {
      this.preloaderChart = false
    }
    if (tab !== undefined) {
      this.changeTab(tab);
      this.getNotes();
    }
    // get affiliate sales
    await this.getAffiliateSales()
    // get advertiser offers
    await this.getAffiliate();

    if (this.platform === 2) {
      const trafficTypesRaw = await affiliates.getTrafficTypes();
      console.log(`trafficTypesRaw`, trafficTypesRaw);

      const excluded = ["Organic", "Media Buy", "Social"];

      this.trafficTypes = trafficTypesRaw
        .map(t => t.name)
        .filter(name => !excluded.includes(name));


      // const trafficTypes = await affiliates.getTrafficTypes();
      // this.trafficTypes = [
      //   'Comparison', 'Display', 'Email', 'Exit Traffic', 'Facebook', 'Google',
      //   'Instagram', 'Native', 'Pop', 'Push', 'SEO', 'Smartlink', 'SMS', 'Survey', 'TikTok',
      //   'Organic', 'Media Buy', 'Social'
      // ];
      const affiliateHubspotTypes = await affiliates.getAffiliateHubspotTypes();
      this.affiliateTypes = affiliateHubspotTypes.map(type => ({
        value: type.id,
        text: type.name
      }));


      if (this.affiliateInfo.trafficTypes) {
        this.selectedTrafficTypes = [...this.affiliateInfo.trafficTypes];
      }
      this.source = this.affiliateInfo.source
      this.companyName = this.affiliateInfo.companyName
      const matchingType = this.affiliateTypes.find(type => type.text === this.affiliateInfo.hubspotAffiliateType);
      this.affiliateType = matchingType ? matchingType.value : null;

    } else {
      this.trafficTypes = ['Organic', 'Media Buy', 'SMS', 'Email', 'Push', 'Pop', 'Social'];
      this.affiliateTypes = [
        {value: 'Publisher', text: 'Publisher'},
        {value: 'Marketer', text: 'Marketer'},
        {value: 'Network', text: 'Network'}
      ]
    }



    if (this.item.tab !== undefined) {
      await this.getNotes();
      this.currentTab = this.item.tab;
    }

    if (this.item.data.verticals.length > 0) {
      this.selectedVerticals = this.item.data.verticals.map( vert =>(
        vert.name
      ));

      this.originVerticals = this.item.data.verticals.map( vert =>(
        vert.name
      ));

    }
    this.newItems =[]
    this.globalLoading = false;
    // get advertiser bills
  },

  methods: {
    ...mapMutations('affiliates', ['saveAffiliateProfileVerticals']),
    showCard(affNameId, defaultTab) {
      const splittedData = affNameId.split('(');
      const item = {
        id: splittedData[1].substring(0, splittedData[1].length - 1),
        name: splittedData[0]
      }

      const path = window.location.host + window.location.pathname
      const affiliateLink = generateLinkWithParams({
        filters: JSON.stringify({
          affNameId: [item.id]
        })
      }, path)

      store.dispatch('cards/ADD', {
        type: 'affiliate',
        title: item.name,
        key: `affiliate-${item.id}`,
        data: {...item, tab: defaultTab},
        affiliateLink
      })
    },
    async openCampaignCard(item) {
      const campaigns = await campaignsApi.getCampaignsData({
        payload: {
          showInactiveCampaigns: true,
          orderBy: 'dateAdded',
          orderDirectionAsc: false,
          page: 1,
          limit: 1,
          filters: {
            id: item.id
          }
        }
      });

      if (!campaigns || !campaigns.data || !campaigns.data.length) {
        return;
      }

      const campaign = campaigns.data[0];

      store.dispatch('cards/ADD', {
        type: 'campaign',
        title: `[${campaign.id}] - [${campaign.name}] : [${campaign.status}]`,
        key: `campaign-${campaign.id}`,
        data: campaign
      })
    },

    addContact() {
      this.affiliateContacts.push({ email: '', isPrimary: false });
    },
    confirmDeleteContact(index) {
      this.selectedContactIndex = index;
      this.dialog = true;
    },
    async resetPassword(email) {
      try {
        const isResetDone = await affiliatesApi.resetPassword({email});

        if (isResetDone) {
          store.dispatch('alert/ALERT_SUCCESS', `Password has been reseted`, {root: true});
        } else {
          store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
        }
      } catch (e) {
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
    },
    deleteContact(index) {
      this.affiliateContacts.splice(index, 1);
      this.dialog = false;
    },
    setPrimaryContact(selectedIndex) {
      this.$set(this.affiliateContacts, selectedIndex, {
        ...this.affiliateContacts[selectedIndex],
        isPrimary: true
      });

      this.affiliateContacts.forEach((contact, index) => {
        if (index !== selectedIndex) {
          this.$set(this.affiliateContacts, index, {
            ...contact,
            isPrimary: false
          });
        }
      });
    },
    getPretyDate(date) {
      const dateObj = DateTime.fromISO(date, {zone: 'UTC'});
      if (!dateObj) {
        return date;
      }
      return dateObj.toFormat('DD');
    },
    removeTrafficType(index) {
      this.selectedTrafficTypes.splice(index, 1);
    },

    async getAffiliate() {
      // get campaigns
      this.affiliateLoading = true;
      this.preloaderAffiliate = true
      let affiliateCampaigns = await campaignsApi.getCampaignsData(
        {
          payload: {
            orderBy: 'dateAdded',
            orderDirectionAsc: false,
            page: 1,
            limit: 1000,
            filters: {affiliateId: this.item.data.id},
            showInactiveCampaigns: false,
        }
      });
      this.cardAffiliate.items = affiliateCampaigns.data
      this.affiliateLoading = false;
      this.preloaderAffiliate = false
      // get affiliate payments
      this.preloaderAccounting = true
      this.cardAffiliateAccounting.items = (await paymentsApi.getAffiliatePayments({
        affiliateId: this.item.data.id
      })).data
      this.preloaderAccounting = false
      // get affiliate information
      this.preloaderInfo = true;
      const affiliateInfo = (await affiliatesApi.getAffiliateInfo({
        affiliateId: this.item.data.id
      }))
      if (affiliateInfo) {
        this.affiliateInfo = affiliateInfo;
        console.warn(`[AFFILIATE_CARD][AffiliateInfo] Loaded info for affiliate ID: ${this.item.data.id}`, this.affiliateInfo);
        this.paymentsBlocked = this.affiliateInfo.paymentsBlocked
      }
      this.preloaderInfo = false;
    },

    async getAffiliateSales() {
      // add condition here in case of custom date period
      const period = {
        fromDate: DateTime.utc().startOf('day').minus({days: this.chartOptions.chartPeriod}).toSeconds(),
        toDate: DateTime.utc().startOf('day').toSeconds()
      };
      const filter = [{field: 'affiliate_id', value: [this.item.data.id]}]
      const params = {
        entity: 'day',
        select: ['day', 'conversions'],
        filter: filter,
        ...period,
        groupBy: 'day',
        orderBy: 'day',
        orderDirectionAsc: false,
        page: 1,
        limit: 100,
        isChart: true,
      };
      this.chartLoading = true;

      let result
      try {
        result = await reports.getDrillDownReports(params);
      } catch (error) {
        this.chartLoading = false
      }

      const { dates, conversions } = createPeriod(result, this.chartOptions.chartPeriod);

      this.chartOptions = {...this.chartOptions, ...{
          xaxis: {
            categories: dates
          }
        }}
      this.series = [{
        data: conversions
      }];
      this.chartLoading = false
    },

    async getPaymentTerms() {
      await store.dispatch('paymentTerms/getPaymentTerms');
      this.paymentTerms = this.terms
              .sort((a,b) => {
                if(a.value < b.value) return -1;
                else return 1
              })
              .map(t => ({value: t.id, text: t.name}));
    },

    async getHistory() {
      this.preloaderHistory = true;
      this.historyTab.items = await affiliates.getAffiliateHistory(this.item.data.id);
      this.preloaderHistory = false;
    },

    async getRiskCases() {
      this.preloaderRisk = true;
      try {
        this.item.data.id
        this.riskTab.items = await affiliates.getRiskCases(this.item.data.id);
      } catch (e) {
        console.error(e);
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
      this.preloaderRisk = false;
    },

    async getNotes() {
      this.preloaderNotes = true;
      try {
        this.item.data.id
        this.noteTab.items = await affiliates.getNotes(this.item.data.id);
      } catch (e) {
        console.error(e);
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
      this.preloaderNotes = false;
    },
    async getAddress() {
      this.preloaderAddress = true;
      try {
        const addressData = await affiliates.getAffiliateAddress({affiliateId: this.item.data.id});
        if (addressData) {
          this.affiliateInfo = {
            ...this.affiliateInfo,
            streetAddress: addressData.streetAddress,
            streetAddress2: addressData.streetAddress2,
            city: addressData.city,
            state: addressData.state,
            postalCode: addressData.postalCode,
            country: addressData.country
          };
        }
      } catch (e) {
        console.error(e);
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
      this.preloaderAddress = false;
    },
    async getAffiliateCap() {
      this.preloaderNotes = true;
      try {

        const capData = await affiliates.getAffiliateCap(this.item.data.id);
        if (capData) {
          this.caps = {
            ...capData,
            isCapEnabled: capData.enabled !== undefined ? capData.enabled : false,
            useDates: capData.useStartEndDate !== undefined ? capData.useStartEndDate : false
          };
        }

        if (!this.getOffers.data || !this.getOffers.data.length) {
          await store.dispatch('offers/saveOffersStore', {
            filters: {},
            token: getCookie('accessToken')
          })
        }
        await this.$store.dispatch('offer/saveExitTrafficOffersStore')
        this.offers = this.getOffers.data.map(offer => {
          if (offer.status !== 'inactive') {
            return {value: offer.id, text: `(${offer.id}) ${offer.name}`}
          }
        }).filter(offer => offer);

        if (this.caps.salesRedirectOfferId) {
          const matchedOffer = this.getExitTrafficOffers.find(
            offer => offer.id === this.caps.salesRedirectOfferId
          );
          this.caps.salesRedirectOfferId = matchedOffer || this.caps.salesRedirectOfferId;
        }
        if (this.caps.clicksRedirectOfferId) {
          const matchedClickOffer = this.getExitTrafficOffers.find(
            offer => offer.id === this.caps.clicksRedirectOfferId
          );
          this.caps.clicksRedirectOfferId = matchedClickOffer || this.caps.clicksRedirectOfferId;
        }
      } catch (e) {
        console.error(e);
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
      this.preloaderNotes = false;
    },
    async createNote() {
      await this.getNotes();
    },

    async updateReferralSettings() {
      try {
        const dataToChange = {};
        if (this.user[0].can('enable_referral_commission')) {
          dataToChange.isCommissionEnabled = this.referralTab.settings.isCommissionEnabled;
        }
        if (this.user[0].can('referral_optional_sales')) {
          dataToChange.isOptionalEarningsEnabled = this.referralTab.settings.isOptionalEarningsEnabled;
        }
        if (this.user[0].can('set_referral_percent')) {
          dataToChange.commission = this.referralTab.settings.commission;
        }
        if(!!dataToChange) {
          try {
            await affiliates.updateReferralSettings(this.item.data.id, dataToChange);
            this.referralTab.settingsJsoned = JSON.stringify(this.referralTab.settings);
            this.referralTab.isSettingsChanged = false;
            store.dispatch('alert/ALERT_SUCCESS', `Commission has been updated`, {root: true});
            setTimeout(() => {
              store.dispatch('alert/ALERT_REMOVE', ``, {root: true});
            }, 3000);
          } catch (err) {
            console.error(err);
            store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
          }
        }
      } catch(err) {
        console.error(err);
      }
    },

    async getPings() {
      this.preloaderPings = true;
      try {
        this.item.data.id
        this.pingsTab.items = await affiliates.getPings(this.item.data.id);
      } catch (e) {
        console.error(e);
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
      this.preloaderPings = false;
    },

    async getProgramsData() {
      this.preloaderPrograms = true;
      try {
        const [verticals, programs, pairs] = await Promise.all([
          verticalsApi.verticals(),
          programsApi.getProgramsList(),
          programsApi.getDefaultPrograms(this.item.data.id)
        ]);
        this.selectPairs = [...pairs];
        this.verticals = verticals;
        this.programs = programs;
      } catch (e) {
        console.error(e);
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
      this.preloaderPrograms = false;
    },
    addNewVertical() {
      if (this.searchTerm && !this.filteredVerticals.some(v => v.name === this.searchTerm)) {
        this.affiliateVerticalsAll.unshift({ name: this.searchTerm });
        this.newItems.push(this.searchTerm);
        this.selectedVerticals.push(this.searchTerm);
        this.searchTerm = '';
      } else if (this.searchTerm) {
        this.error = 'This vertical already exists';
      }
    },
    async updateAction(){
      const addedVerticals = this.selectedVerticals.filter(vertical => !this.originVerticals.includes(vertical));
      const removedVerticals = this.originVerticals.filter(vertical => !this.selectedVerticals.includes(vertical));
      const data  = {
        id: this.item.data.id,
        added: addedVerticals,
        removed: removedVerticals,
        newItems: this.newItems,
        toSave: this.selectedVerticals,
        origin: this.originVerticals
      }
      const selectedVerticalsObject = [];
      this.selectedVerticals.forEach((selectedVertical) => {
        const foundVertical = this.affiliateVerticalsAll.find((affiliateVertical) => {
          return affiliateVertical.name === selectedVertical;
        });

        if (foundVertical) {
          selectedVerticalsObject.push({
            "id": foundVertical.id,
            "name": selectedVertical
          });
        }
      });

      const isSuccess = await affiliatesApi.updateAffiliateVerticals(data);
      if (isSuccess){
        await store.dispatch('alert/ALERT_SUCCESS', `Verticals updated successfully`, {root: true});
        this.originVerticals = this.selectedVerticals.map(vert=>(vert));
        this.newItems = [];
        const vertical =  {
          affiliateId: this.item.data.id,
          verticals: selectedVerticalsObject,
        }
        await this.saveAffiliateProfileVerticals(vertical);
      } else {
        await store.dispatch('alert/ALERT_ERROR', `Verticals updated got error.`, {root: true});
      }

    },
    updateSelectedVerticals(selectedItems) {
      this.error ='';
      console.log('updateSelectedVerticals',this.affiliateVerticalsAll)
    },
    async getTopCountries() {
      this.preloaderTopCountries = true;
      try {
        this.allCountries = await affiliatesApi.getTopCountries();

        const topCountriesString = this.affiliateInfo.topCountries || ''; // "CH, IT"
        const countryCodes = topCountriesString
          .split(',')
          .map(code => code.trim())
          .filter(Boolean);

        this.originCountries = countryCodes
          .map(code =>
            this.allCountries.find(c => c.code?.toUpperCase() === code.toUpperCase())
          )
          .filter(Boolean);

        this.selectedCountries = [...this.originCountries];

      } catch (e) {
        console.error('getTopCountriesError:', e);
        this.countryError = 'Failed to load countries';
      }
      this.preloaderTopCountries = false;
    },
    removeCountry(index) {
      this.selectedCountries.splice(index, 1);
    },
    async updateCountriesAction() {
      try {

        const normalizedSelected = this.selectedCountries.map(code => {
          if (typeof code === 'string') {
            return this.allCountries.find(c => c.code === code) || { code, name: code };
          }
          return code;
        });

        const added = normalizedSelected.filter(
          c => !this.originCountries.find(o => o.code === c.code)
        );
        const removed = this.originCountries.filter(
          c => !normalizedSelected.find(s => s.code === c.code)
        );

        const data = {
          affiliateId: this.item.data.id,
          added: added.map(c => c.code),
          removed: removed.map(c => c.code),
          newItems: this.newCountries.map(c => c.name),
        };


        const result = await affiliatesApi.updateAffiliateTopCountries(data);

        if (result) {
          store.dispatch('alert/ALERT_SUCCESS', 'Top countries updated successfully', { root: true });
          this.originCountries = [...normalizedSelected];
          this.selectedCountries = [...normalizedSelected]; // обновим и их, чтобы не потерять формат
          this.newCountries = [];
        } else {
          throw new Error();
        }
      } catch (e) {
        console.error(e);
        store.dispatch('alert/ALERT_ERROR', 'Failed to update top countries', { root: true });
      }
    },

    async getAffiliateVerticalsData() {
      this.preloaderAffVerticals = true;
      try {
        const [affVerticalsAll] = await Promise.all([
          affiliatesApi.getAffiliateVerticals(),
        ]);
        this.affiliateVerticalsAll = affVerticalsAll.map(vert => ({
          id: vert.id,
          name: vert.name
        }))

      } catch (e) {
        console.error(e);
        await store.dispatch('alert/ALERT_ERROR', `getAffiliateVerticalsData error.`, {root: true});
      }
      this.preloaderAffVerticals = false;
    },
    removeVertical(index) {
      this.selectedVerticals.splice(index, 1);
    },
    addSelectPair() {
      if (this.selectPairs.every((pair) => pair.vertical && pair.program)) {
        this.selectPairs.push({ vertical: null, program: null });
      }
    },

    deleteSelectPair(index) {
      this.selectPairs.splice(index, 1);
    },

    hasDuplicatesDefaultPrograms(array) {
      if (!array.length) return false;
      const seenVertical = new Set();
      const seenProgram = new Set();

      for (const item of array) {
        const keyVertical = item.vertical;
        const keyProgram = item.program;
        if (seenVertical.has(keyVertical) || seenProgram.has(keyProgram)) {
          return true;
        }

        seenVertical.add(keyVertical);
        seenProgram.add(keyProgram);
      }

      return false;
    },

    async saveDefaultPrograms() {
      if (this.hasDuplicatesDefaultPrograms(this.selectPairs)) {
        this.showDefaultProgramError = true;
        setTimeout(()=>{this.showDefaultProgramError = false}, 1000)
      }
      try {
        const result = await programsApi.setDefaultPrograms(this.selectPairs, this.item.data.id);
        if (result) {
          store.dispatch('alert/ALERT_SUCCESS', `Default program(s) has been created`, {root: true});
        } else {
          store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
        }
      } catch (e) {
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }
    },

    async getReferralSettings() {
      const result = await affiliatesApi.fetchReferralSettings(this.item.data.id);
      this.referralTab.settings.isCommissionEnabled = result.isCommissionEnabled;
      this.referralTab.settings.isOptionalEarningsEnabled = result.isOptionalEarningsEnabled;
      this.referralTab.settings.commission = result.commission;
      this.referralTab.family = {
        parent: result.parent,
        children: result.referrals
      }
      this.referralTab.settingsJsoned = JSON.stringify(this.referralTab.settings);
    },
    isAffiliateContactsChanged() {
      return JSON.stringify(this.affiliateContacts) !== JSON.stringify(this.affiliateContactsOrigin);
    },
    findDuplicateEmail(contacts){
      const emails = contacts.map(contact => contact.email);
      const duplicates = emails.filter((email, index) => emails.indexOf(email) !== index);
      return duplicates.length > 0 ? duplicates : [];
    },
    async onUpdateAffiliateContacts() {
      try {
        let validation = [];

        this.affiliateContacts = this.affiliateContacts.map(contact => {
          return {
            ...contact,
            email: contact.email.replace(/\s+/g, '')
          };
        });

        const duplicateEmails = this.findDuplicateEmail(this.affiliateContacts);

        if (duplicateEmails.length > 0) {
          validation.push(`Duplicate emails found: ${duplicateEmails.join(', ')}`);
          this.validation = validation;
          return;
        }

        const newContacts = this.affiliateContacts.filter(contact => {
          return !this.affiliateContactsOrigin.some(originContact => originContact.email === contact.email);
        });

        for (const contact of newContacts) {
          const exists = await affiliates.isEmailExists(contact.email);
          if (exists) {
            throw new Error(`Email ${contact.email} already exists.`);
          }

          if (this.affiliateContacts.filter(c => c.email === contact.email).length > 1) {
            validation.push(`Duplicate email found: ${contact.email}`);
          }
        }

        if (validation.length > 0) {
          this.validation = validation;
          return;
        }

        const resp = await affiliates.insertOrUpdateAffiliateContacts(this.item.data.id, this.affiliateContacts);

        if (resp) {
          await store.dispatch('alert/ALERT_SUCCESS', `Affiliate contacts have been updated`, { root: true });
          this.affiliateContactsOrigin = [...this.affiliateContacts];
          this.validation = [];
        } else {
          await store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, { root: true });
        }
        setTimeout(() => {
          store.dispatch('alert/ALERT_REMOVE', ``, { root: true });
        }, 3000);
      } catch (e) {
        console.error("Error updating affiliate contacts:", e);
        this.validation = [e.message]; // Set validation error message
      }
    },
    async onUpdate() {
      this.updating = true;
      const currency = this.currencies.find(c => c.id === this.currency);
      this.minAmountOverride = this.minAmountOverride === '' ? null : this.minAmountOverride;
      const updatedData = {
        id: this.item.data.id,
        name: this.name,
        email: this.email,
        status: this.status,
        paymentType: this.item.data.paymentType,
        currency: currency.symbol,
        currencyId: currency.id,
        currencyName: currency.name,
        billingCycle: this.billingCycle,
        affiliateManagerId: this.managerId,
        postbackUrl: this.postbackUrl,
        verified: this.item.data.verified,
        affiliateType: this.affiliateType,
        trafficType: this.trafficType,
        paymentTermId: this.paymentTermId,
        minAmountOverride: this.minAmountOverride
      };
      const updatedAffiliateInfoData = {
        id: this.item.data.id,
        paymentsBlocked: this.paymentsBlocked
      }
      try {
        await store.dispatch('affiliates/saveAffiliateProfile', updatedData);
        if (this.paymentsBlocked !== this.affiliateInfo.paymentsBlocked) {
          await store.dispatch('affiliates/saveAffiliateInfo', updatedAffiliateInfoData);
        }
        if (this.platform === 2){
          const updatedAffiliateInfoDataGotzha = {
            id: this.item.data.id,
            trafficTypes: this.selectedTrafficTypes.join(', '),
            companyName: this.companyName || ``,
            affiliateType: this.affiliateType || ``,
            source: this.source ||``,
          }

          await store.dispatch('affiliates/saveAffiliateInfoGotzha', updatedAffiliateInfoDataGotzha);

          await store.dispatch('affiliates/syncUserHubspotGotzha',this.item.data.id);
        }
        store.dispatch('cards/REMOVE', `affiliate-${this.item.data.id}`);
        store.dispatch('alert/ALERT_SUCCESS', `Affiliate has been updated`, {root: true});
      } catch (e) {
        store.dispatch('alert/ALERT_ERROR', `An error has occurred.`, {root: true});
      }

      setTimeout(() => {
        store.dispatch('alert/ALERT_REMOVE', ``, {root: true});
      }, 3000);

      await affiliatesApi.updateAffiliatePostbacks({affiliateId: this.item.data.id, postbacks: this.postbackUrls.filter(Boolean)});

      this.updating = false;
    },

    changeTab(tabN) {
      this.currentTab = tabN
    },
    updStart(date) {
      this.caps.startDate = date;
    },
    updEnd(date) {
      this.caps.endDate = date;
    },
    async exitTrafficOffersUpd(event) {
      this.debounceUpdateExitOffers(event)
    },
    debounceUpdateExitOffers: debounce(async (search) => {
      await store.dispatch('offer/saveExitTrafficOffersStore', {search})
    }, 700),
    async updateCaps() {
      const data = {
        id: this.caps.id,
        affiliateId: this.item.data.id,
        clicksDay: this.caps.clicksDay,
        clicksWeek: this.caps.clicksWeek,
        clicksMonth: this.caps.clicksMonth,
        clicksRedirectOfferId: this?.caps?.clicksRedirectOfferId?.id || 0,
        salesDay: this.caps.salesDay,
        salesWeek: this.caps.salesWeek,
        salesMonth: this.caps.salesMonth,
        salesRedirectOfferId: this?.caps?.salesRedirectOfferId?.id || 0,
        startDate: this.caps.startDate,
        endDate: this.caps.endDate,
        enabled: this.caps.isCapEnabled,
        clicksRedirectStatus: 'default',
        salesRedirectStatus: 'default',
        useStartEndDate: this.caps.useDates,
      };

      const response = await affiliatesApi.updateAffiliateCap(data)
      if (response) {
        console.log('Updated successfully', response);
        const updatedOfferSales = this.getExitTrafficOffers.find(
          offer => offer.id === response.salesRedirectOfferId
        );
        const updatedOfferClicks = this.getExitTrafficOffers.find(
          offer => offer.id === response.clicksRedirectOfferId
        );
        this.caps = {
          id: response.id,
          isCapEnabled: response.enabled,
          useDates: response.useStartEndDate,
          startDate: response.startDate,
          endDate: response.endDate,
          salesDay: response.salesDay,
          salesWeek: response.salesWeek,
          salesMonth: response.salesMonth,
          salesRedirectOfferId: updatedOfferSales,
          clicksDay: response.clicksDay,
          clicksWeek: response.clicksWeek,
          clicksMonth: response.clicksMonth,
          clicksRedirectOfferId: updatedOfferClicks,
        };
        await this.$dialog.notify.success(`Updated successfully `, {
          position: 'top-right',
          timeout: 1000
        })
      } else {
        await this.$dialog.notify.error(`Failed to update affiliate cap`, {
          position: 'top-right',
          timeout: 1000
        })
        console.error('Failed to update affiliate cap');
      }
    },
    async manageNote(note, action) {
      if (action === 'delete') {
        await affiliatesApi.updateNote({id: note.id, note: note.note, isDelete: true});
        await this.getNotes();
      }
    },

    addPostbackUrl() {
      this.postbackUrls.push('');
    },
    removePostbackUrl(index) {
      this.postbackUrls.splice(index, 1);
    }

  },
  computed: {
    ...mapGetters('affiliates', ['getAffiliatesProfileCO']),
    ...mapGetters('offers', ['getOffers']),
    ...mapGetters('offer', ['getExitTrafficOffers']),
    ...mapGetters('paymentTerms', ['terms']),
    ...mapGetters('managers', ['affiliateManagers']),
    ...mapGetters('user', ['getUser']),
    ...mapState('user', ['user']),
    isApprovalLocked() {
      return ['approval', 'rejected'].includes(this.item.data.status) && !this.user[0].can('review_approval_users');
    },
    isUpdateDisabled() {
      return this.isApprovalLocked || JSON.stringify(this.originVerticals) === JSON.stringify(this.selectedVerticals);
    },
    combinedCountries() {

      const combinedMap = new Map();

      const selectedCodes = this.selectedCountries.map(c =>
        typeof c === 'string' ? c : c.code
      );

      const originMap = new Map(this.originCountries.map(c => [c.code, c.name]));

      for (const code of selectedCodes) {
        combinedMap.set(code, {
          code,
          name: originMap.get(code) || code
        });
      }

      const combined = Array.from(combinedMap.values());
      return combined;
    },

    isCountriesUpdateDisabled() {
      const codes1 = this.selectedCountries.map(c => c.code).sort();
      const codes2 = this.originCountries.map(c => c.code).sort();
      return this.isApprovalLocked || JSON.stringify(codes1) === JSON.stringify(codes2);
    },
    filteredCountriesList() {
      if (!this.searchCountryTerm) return this.allCountries;
      return this.allCountries.filter(country =>
        country.code.toLowerCase().includes(this.searchCountryTerm.toLowerCase())
      );
    },
    filteredVerticals() {
      if (!this.searchTerm) {
        return this.affiliateVerticalsAll;
      }

      return this.affiliateVerticalsAll.filter((vertical) => {
        return vertical.name.toLowerCase().includes(this.searchTerm.toLowerCase());
      });
    },
    combinedTier() {
      return `${this.item.data.tier} / ${this.item.data.highest_tier}`;
    },
    noteTabName() {
      if (this.currentTab !== 6) {
        return 'notes'
      }
      return 'add notes'
    },
    checkInputsNotChanged() {
      return this.status === this.item.data.status &&
          this.name === this.item.data.name &&
          this.billingCycle === this.item.data.billingCycle &&
          this.managerId === this.item.data.affiliateManagerId &&
          this.postbackUrl === this.item.data.postbackUrl &&
          this.currency === this.item.data.currency &&
          this.affiliateType === this.item.data.affiliateType &&
          this.paymentsBlocked === this.affiliateInfo.paymentsBlocked &&
          this.trafficType === this.item.data.trafficType &&
          this.minAmountOverride === this.item.data.minAmountOverride &&
          this.paymentTermId === this.item.data.paymentTermId &&
          JSON.stringify(this.postbackUrls) === JSON.stringify(this.initialPostbacks)
    },
    sliderSettings() {
      //const isProdOne = process.env.VUE_APP_ENV === 'prod-1';
      const isProdOne = window.location.hostname.includes('aezai');

      // console.log('[IS PROD 1 ?]', isProdOne, ' process.env.VUE_APP_ENV', process.env.VUE_APP_ENV);
      // console.log('[IS PROD 1 ?]', isProdOne, ' process.env.APP_ENV', process.env.APP_ENV);
      return {
        min: isProdOne ? this.referralTab.settings.minCommission : 0,
        max: isProdOne ? this.referralTab.settings.maxCommission : 100,
        step: isProdOne ? 1 : 0.5
      };
    }
  },
  watch: {
    affiliateContacts: {
      deep: true,
      handler() {
        this.isAffiliateContactsChanged();
      }
    },
    currentTab() {
      console.log(this.currentTab);
    },
    referralTab: {
      deep: true,
      handler: (tab) => {
        if(tab.settingsJsoned !== JSON.stringify(tab.settings)) {
          return tab.isSettingsChanged = true;
        }
        return tab.isSettingsChanged = false;
      }
    }
  },
  name: "AffiliateCard",
  data() {
    return {
      globalLoading: true,
      preloaderTopCountries: false,
      selectedCountries: [],
      originCountries: [],
      allCountries: [],
      searchCountryTerm: '',
      newCountries: [],
      countryError: '',
      caps: {
        isCapEnabled: false,
        useDates: false,
        startDate: null,
        endDate: null,
        salesDay: 0,
        salesWeek: 0,
        salesMonth: 0,
        salesRedirectOfferId: null,
        clicksDay: 0,
        clicksWeek: 0,
        clicksMonth: 0,
        clicksRedirectOfferId: null,
      },
      offers: [],
      tabMap: {
        0: 'General',
        1: 'Info',
        2: 'Campaigns',
        3: 'Accounting',
        4: 'History',
        5: 'Risk',
        6: 'Notes',
        7: 'DefaultProgram'
      },
      selectedVerticals: [],
      selectedTrafficTypes: [],
      companyName: '',
      source: '',
      postbackUrls: [],
      initialPostbacks: [],
      originVerticals: [],
      dialog: false,
      selectedContactIndex: null,
      currentTab: 0,
      managers: [],
      paymentTerms: [],
      validation: [],
      paymentTermId: this.item.data.paymentTermId,
      managerId: this.item.data.affiliateManagerId,
      name: this.item.data.name,
      email: this.item.data.email,
      billingCycle: this.item.data.billingCycle,
      currency: this.item.data.currency,
      status: this.item.data.status,
      minAmountOverride: this.item.data.minAmountOverride,
      affiliateType: this.item.data.affiliateType,
      trafficType: this.item.data.trafficType,
      postbackUrl: this.item.data.postbackUrl,
      trafficTypes:[],
      statuses: [
        {value: 'active', text: 'Active'}, {value: 'pending', text: 'Pending'}, {value: 'approval', text: 'Approval'},
        {value: 'rejected', text: 'Rejected'}, {value: 'suspended', text: 'Suspended'}, {value: 'blocked', text: 'Blocked'},  {value: 'blacklisted', text: 'Blacklisted'}
      ],
      affiliateTypes: [],
      updating: false,
      preloaderHistory: false,
      preloaderReferral: false,
      preloaderChart: false,
      preloaderAffiliate: false,
      preloaderInfo: false,
      preloaderAccounting: false,
      preloaderRisk: true,
      preloaderNotes: true,
      preloaderPings: true,
      preloaderPrograms: true,
      preloaderAffVerticals: true,
      preloaderAddress: false,
      chartLoading: true,
      affiliateLoading: true,
      accountingLoading: true,
      showDefaultProgramError: false,
      platform: null,
      affiliateContacts: null,
      affiliateContactsOrigin: null,
      // start tem
      cardAffiliate: {
        headers: [
          { text: 'Id', value: 'id', class: 'color', align: 'center' },
          { text: 'Name', value: 'name', align: 'left' },
          { text: 'Offer', value: 'offerName', align: 'left' },
          { text: 'Status', value: 'status', align: 'center' },
          { text: 'Currency', value: 'currency', align: 'center' },
          { text: 'PayIn', value: 'payIn', align: 'center' },
          { text: 'PayOut', value: 'payOut', align: 'center' },
          { text: 'Last Sale Date', value: 'lastSaleDate', align: 'center' },
          { text: 'Date Added', value: 'dateAdded', align: 'center' },
        ],
        items: [],
        pageOptions: [10, 25, 50, 100],
      },
      cardAffiliateAccounting: {
        headers: [
          { text: 'Status', value: 'status', class: 'color', align: 'center' },
          { text: 'Billing Period', value: 'billing_cycle', class: 'color', align: 'center' },
          { text: 'Start period', value: 'start_period', class: 'color', align: 'center' },
          { text: 'End period', value: 'end_period', class: 'color', align: 'center' },
          { text: 'Sales Revenue', value: 'amount', align: 'center' },
          { text: 'Referral Revenue', value: 'referral_commission', align: 'center' },
          { text: 'Modifications', value: 'modifications', align: 'center' },
          { text: 'Carry Over', value: 'carry_over_amount', align: 'center' },
          { text: 'Total Revenue', value: 'total_revenue', align: 'center' },
          { text: 'Paid', value: 'partial_payments', align: 'center' },
        ],
        items: [],
        pageOptions: [10, 25, 50, 100],
      },
      riskTab: {
        headers: [
          {text: 'Status', value: 'status', class: 'color', align: 'center'},
          {text: 'Reason', value: 'reason', class: 'color', align: 'center'},
          {text: 'Campaign', value: 'campaignName', class: 'color', align: 'center'},
          {text: 'Reporter', value: 'initiator', class: 'color', align: 'center'},
          {text: 'Date Added', value: 'dateAdded', align: 'center'},
        ],
        items: [],
        pageOptions: [10, 25, 50, 100],
      },
      noteTab: {
        headers: [
          {text: 'Author', value: 'author', class: 'color', align: 'center'},
          {text: 'Author Email', value: 'email', class: 'color', align: 'center'},
          {text: 'Note', value: 'note', class: 'color', align: 'center', cellClass:'noteCell'},
          {text: 'Date Added', value: 'dateAdded', align: 'center'},
        ],
        items: [],
        pageOptions: [10, 25, 50, 100],
      },
      pingsTab: {
        headers: [
          {text: 'Date Added', value: 'dateAdded', align: 'center'},
          {text: 'Url', value: 'url', class: 'color', align: 'center'},
          {text: 'Status Code', value: 'statusCode', class: 'color', align: 'center'},
          {text: 'Attempts', value: 'attempts', class: 'color', align: 'center'},
          {text: 'Last Attempt', value: 'lastAttempt', align: 'center'},
        ],
        items: [],
        pageOptions: [10, 25, 50, 100],
      },
      referralTab: {
        settings: {
          isCommissionEnabled: false,
          isOptionalEarningsEnabled: false,
          commission: 5,
          maxCommission: 10,
          minCommission: 5
        },
        isSettingsChanged: false,
        settingsJsoned: '',
        family: {
          parent: '',
          children: []
        }
      },
      affiliateInfo: {
        firstName: null,
        lastName: null,
        verticals: [],
        websites: [],
        trafficTypes: [],
        otherNetworks: [],
        topPromotedBrands: null,
        topCountries: null,
        learnSource: null,
        expectedMonthlyRevenue: null,
        imType: null,
        imUsername: null,
        companyName:'',
        source: ``,
        hubspotAffiliateType: ``,
        paymentsBlocked: false
      },
      chartOptions: {
        chartPeriod: 7,
        chart: {
          id: `affiliate-${this.item.data.id}-chart`,
          toolbar: {
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
        },
        xaxis: {
          categories: []
        },
        colors: [this.$vuetify.theme.themes.light.primary],
      },
      series: [
        {
          name: 'Conversions',
          data: []
        },
      ],
      historyTab: {
        headers: [
          {text: 'User', align: 'start', sortable: false, value: 'user'},
          {text: 'Action', sortable: false, value: 'action'},
          {text: 'Date', sortable: false, value: 'dateAdded'},
          {text: 'Logs', sortable: false, value: 'logs'},
        ],
        items: [],
        pageOptions: [10, 25, 50, 100],
      },
      paymentsBlocked: false,
      selectPairs: [],
      affiliateVerticalsAll: [],
      error: "",
      searchTerm: "",
      newItemName: "",
      newItems: [],
      affiliateVerticals: [],
      verticals: [],
      programs: [],
      currencies: [],
    }
  }
}
</script>

<style lang="sass">
.save-btn
  margin-top: 10px
.custom-header
  font-weight: bold
.btn-add
  color: var(--v-primary-base) !important
  font-weight: bold
.v-input--switch
  padding: 0 0 2px 0
  .v-input__control
    .v-input__slot
      margin: 0
    .v-messages
      display: none !important

.subtitle
  font-weight: 700
  color: #808080
.noteCell
  white-space: pre

.small-button
  font-size: 12px
  padding: 4px 8px
  min-width: auto

.selected-verticals
  padding: 10px
  border: 1px solid #ddd
  border-radius: 5px
  background-color: #f9f9f9
  margin-top: 10px
  display: flex
  flex-wrap: wrap
  gap: 10px


.vertical-item
  padding: 5px 10px
  border: 1px solid #ccc
  border-radius: 15px
  background-color: #e0e0e0
  color: #333
  font-size: 14px
  white-space: nowrap

.campaign-item
  color: var(--v-primary-base) !important
  &:hover
    cursor: pointer
    text-decoration: underline

.spinner-overlay
  position: fixed
  top: 0
  left: 0
  width: 100vw
  height: 100vh
  z-index: 9999
  display: flex
  justify-content: center
  align-items: center
  pointer-events: none
  background-color: rgba(255, 255, 255, 0.5)

</style>
